import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Layers, BarChart3, Settings, LogOut, Plus, Edit, Trash2, 
  Check, X, ChevronDown, Search, Shield, User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPlatform, setEditingPlatform] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, platformsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/platforms`, { withCredentials: true }),
        axios.get(`${API_URL}/api/stats`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setPlatforms(platformsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E60000]/30 border-t-[#E60000] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#111] border-r border-white/10 p-6">
        <div className="font-heading text-2xl font-black mb-8">
          Sente<span className="text-[#E60000]">tica</span>
        </div>
        
        <nav className="space-y-2">
          {[
            { id: 'users', icon: Users, label: 'Users' },
            { id: 'platforms', icon: Layers, label: 'Platforms' },
            { id: 'stats', icon: BarChart3, label: 'Statistics' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-[#E60000] text-white' 
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg mb-3">
            <div className="w-8 h-8 rounded-full bg-[#E60000] flex items-center justify-center">
              <Shield size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-gray-500">Admin</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {activeTab === 'users' && (
          <UsersTab 
            users={users} 
            platforms={platforms}
            onRefresh={loadData}
            showModal={showUserModal}
            setShowModal={setShowUserModal}
            editingUser={editingUser}
            setEditingUser={setEditingUser}
          />
        )}
        {activeTab === 'platforms' && (
          <PlatformsTab 
            platforms={platforms}
            onRefresh={loadData}
            showModal={showPlatformModal}
            setShowModal={setShowPlatformModal}
            editingPlatform={editingPlatform}
            setEditingPlatform={setEditingPlatform}
          />
        )}
        {activeTab === 'stats' && (
          <StatsTab stats={stats} users={users} />
        )}
      </main>
    </div>
  );
};

// ============== USERS TAB ==============
const UsersTab = ({ users, platforms, onRefresh, showModal, setShowModal, editingUser, setEditingUser }) => {
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', role: 'customer', platforms: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingUser) {
      setFormData({
        email: editingUser.email,
        password: '',
        name: editingUser.name,
        role: editingUser.role,
        platforms: editingUser.platforms || []
      });
      setShowModal(true);
    }
  }, [editingUser]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        delete updateData.email;
        await axios.put(`${API_URL}/api/admin/users/${editingUser._id}`, updateData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/users`, formData, { withCredentials: true });
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ email: '', password: '', name: '', role: 'customer', platforms: [] });
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const togglePlatform = (platformId) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Users</h1>
        <motion.button
          onClick={() => { setEditingUser(null); setFormData({ email: '', password: '', name: '', role: 'customer', platforms: [] }); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#E60000] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#CC0000] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          Add User
        </motion.button>
      </div>

      {/* Users Table */}
      <div className="bg-[#111] rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">User</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Role</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Platforms</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Usage</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-sm text-gray-500">{u.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    u.role === 'admin' ? 'bg-[#E60000]/20 text-[#E60000]' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {u.role === 'admin' ? <Shield size={12} /> : <User size={12} />}
                    {u.role}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400">
                    {u.role === 'admin' ? 'All' : (u.platforms?.length || 0)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400">
                    {u.usage_count || 0} / {u.usage_limit === -1 ? '∞' : u.usage_limit}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => setEditingUser(u)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  {u.role !== 'admin' && (
                    <button
                      onClick={() => handleDelete(u._id)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors ml-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#111] rounded-2xl p-6 w-full max-w-md border border-white/10"
          >
            <h2 className="text-xl font-bold mb-6">{editingUser ? 'Edit User' : 'Add User'}</h2>
            
            <div className="space-y-4">
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                    placeholder="user@example.com"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {editingUser ? 'New Password (leave empty to keep)' : 'Password'}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                >
                  <option value="customer">Customer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              {formData.role === 'customer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Platforms (multi-select)</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                    {platforms.length === 0 ? (
                      <p className="text-gray-500 text-sm">No platforms available. Add platforms first.</p>
                    ) : (
                      platforms.map((p) => (
                        <label key={p._id} className="flex items-center gap-3 cursor-pointer hover:bg-white/5 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.platforms.includes(p._id)}
                            onChange={() => togglePlatform(p._id)}
                            className="w-4 h-4 rounded border-white/20 bg-transparent text-[#E60000] focus:ring-[#E60000]"
                          />
                          <span className="text-sm">{p.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setEditingUser(null); }}
                className="flex-1 px-4 py-3 border border-white/10 text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-[#E60000] text-white rounded-lg font-medium hover:bg-[#CC0000] transition-colors disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {saving ? 'Saving...' : 'Save'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// ============== PLATFORMS TAB ==============
const PlatformsTab = ({ platforms, onRefresh, showModal, setShowModal, editingPlatform, setEditingPlatform }) => {
  const [formData, setFormData] = useState({
    name: '', base_url: '', auth_mode: 'jwt', access_key: '', secret_key: '', api_key: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingPlatform) {
      setFormData({
        name: editingPlatform.name,
        base_url: editingPlatform.base_url,
        auth_mode: editingPlatform.auth_mode || 'jwt',
        access_key: editingPlatform.access_key || '',
        secret_key: editingPlatform.secret_key || '',
        api_key: editingPlatform.api_key || ''
      });
      setShowModal(true);
    }
  }, [editingPlatform]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingPlatform) {
        await axios.put(`${API_URL}/api/admin/platforms/${editingPlatform._id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/platforms`, formData, { withCredentials: true });
      }
      setShowModal(false);
      setEditingPlatform(null);
      setFormData({ name: '', base_url: '', auth_mode: 'jwt', access_key: '', secret_key: '', api_key: '' });
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving platform');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (platformId) => {
    if (!window.confirm('Are you sure you want to delete this platform?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/platforms/${platformId}`, { withCredentials: true });
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting platform');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Platforms</h1>
        <motion.button
          onClick={() => { setEditingPlatform(null); setFormData({ name: '', base_url: '', auth_mode: 'jwt', access_key: '', secret_key: '', api_key: '' }); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#E60000] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#CC0000] transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          Add Platform
        </motion.button>
      </div>

      {/* Platforms Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {platforms.map((p) => (
          <div key={p._id} className="bg-[#111] rounded-xl border border-white/10 p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#E60000]/20 flex items-center justify-center">
                <Layers size={20} className="text-[#E60000]" />
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setEditingPlatform(p)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => handleDelete(p._id)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <h3 className="font-bold text-lg mb-1">{p.name}</h3>
            <p className="text-sm text-gray-500 truncate">{p.base_url}</p>
            <span className="inline-block mt-3 px-2 py-1 bg-white/5 rounded text-xs text-gray-400">
              {p.auth_mode}
            </span>
          </div>
        ))}
      </div>

      {/* Platform Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#111] rounded-2xl p-6 w-full max-w-md border border-white/10 max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-xl font-bold mb-6">{editingPlatform ? 'Edit Platform' : 'Add Platform'}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Platform Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                  placeholder="Kling AI"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Base URL</label>
                <Input
                  type="text"
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                  placeholder="https://api.klingapi.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Auth Mode</label>
                <select
                  value={formData.auth_mode}
                  onChange={(e) => setFormData({ ...formData, auth_mode: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                >
                  <option value="jwt">JWT (Access Key + Secret Key)</option>
                  <option value="apiKey">API Key</option>
                </select>
              </div>
              
              {formData.auth_mode === 'jwt' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Access Key</label>
                    <Input
                      type="text"
                      value={formData.access_key}
                      onChange={(e) => setFormData({ ...formData, access_key: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                      placeholder="Your access key"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Secret Key</label>
                    <Input
                      type="password"
                      value={formData.secret_key}
                      onChange={(e) => setFormData({ ...formData, secret_key: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                      placeholder="Your secret key"
                    />
                  </div>
                </>
              )}
              
              {formData.auth_mode === 'apiKey' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">API Key</label>
                  <Input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
                    placeholder="Your API key"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setEditingPlatform(null); }}
                className="flex-1 px-4 py-3 border border-white/10 text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-[#E60000] text-white rounded-lg font-medium hover:bg-[#CC0000] transition-colors disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {saving ? 'Saving...' : 'Save'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// ============== STATS TAB ==============
const StatsTab = ({ stats, users }) => {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Statistics</h1>
      
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {[
          { label: 'Total Users', value: stats.total_users || 0, color: 'bg-blue-500' },
          { label: 'Total Platforms', value: stats.total_platforms || 0, color: 'bg-[#E60000]' },
          { label: 'Total Generations', value: stats.total_generations || 0, color: 'bg-green-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#111] rounded-xl border border-white/10 p-6">
            <div className={`w-10 h-10 rounded-lg ${stat.color}/20 flex items-center justify-center mb-4`}>
              <BarChart3 size={20} className={stat.color.replace('bg-', 'text-')} />
            </div>
            <p className="text-3xl font-bold">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
